/**
 * IntelShareCard — 1080×1080 card captured by html2canvas.
 * Parses AI question text into: topic label + context statement + the question.
 * Visual hierarchy makes the card impactful rather than a wall of text.
 */
import React from 'react';

// Gold (#C5A52A) for all smaller text across every template — works on grey, red, and gold backgrounds
const TEMPLATE_ACCENTS = [
  { question: '#ffffff', context: '#C5A52A', label: '#C5A52A' },
  { question: '#ffffff', context: '#C5A52A', label: '#C5A52A' },
  { question: '#ffffff', context: '#C5A52A', label: '#C5A52A' },
];

/**
 * Splits an AI question string into:
 *   topic    — "EX26-20 (Water & Wastewater Master Plans)"
 *   context  — "The committee recommended delegating authority…"
 *   question — "What specific internal expertise has been considered…?"
 */
function parseIntelText(raw) {
  const text = raw.replace(/:\*\*/g, ':').replace(/\*\*/g, '').replace(/\*/g, '').replace(/^["']|["']$/g, '').trim();

  // Extract "Regarding TOPIC:" prefix
  const regardingMatch = text.match(/^Regarding\s+([^:]{3,80}):\s*/i);
  const topic = regardingMatch
    ? regardingMatch[1].replace(/^["']+|["']+$/g, '').trim()
    : '';
  const remainder = regardingMatch ? text.slice(regardingMatch[0].length) : text;

  // Find where the actual question starts
  const qStart = remainder.search(/\b(What|Why|How|Can|Could|Is|Are|Did|Does|When|Where|Who|Given|If)\b/i);

  if (qStart > 5) {
    const context = remainder.slice(0, qStart).replace(/^["'\s]+|[.,"'\s]+$/g, '').trim();
    const question = remainder.slice(qStart).replace(/["']+$/, '').trim();
    return { topic, context, question };
  }

  // No clear split — show everything as the question
  return { topic, context: '', question: remainder.replace(/^["']+|["']+$/g, '').trim() };
}

const IntelShareCard = React.forwardRef(
  ({ question, username, committee, meetingDate, templateIndex = 0 }, ref) => {
    const src = `/templates/template_${(templateIndex % 3) + 1}.png`;
    const acc = TEMPLATE_ACCENTS[templateIndex % 3];
    const shadow = '0px 2px 18px rgba(0,0,0,0.98), 0px 0px 8px rgba(0,0,0,0.95)';

    const { topic, context, question: q } = parseIntelText(question);

    // Dynamic font size for the question based on length
    const qFontSize = q.length > 180 ? 42 : q.length > 120 ? 50 : q.length > 80 ? 58 : 66;

    return (
      <div
        ref={ref}
        style={{
          width: 1080,
          height: 1080,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
          background: '#000',
        }}
      >
        {/* Full-size background template */}
        <img
          src={src}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />

        {/* Text block — centred inside the frame (frame starts ~65px from edges, logo at bottom 170px) */}
        <div
          style={{
            position: 'absolute',
            top: 80, left: 90, right: 90,
            bottom: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 22,
          }}
        >
          {/* Committee + date */}
          <p style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: 24,
            fontFamily: 'Arial, sans-serif',
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            textAlign: 'center',
            textShadow: shadow,
            margin: 0,
          }}>
            {[committee, meetingDate].filter(Boolean).join(' · ')}
          </p>

          {/* Topic label */}
          {topic && (
            <p style={{
              color: acc.label,
              fontSize: 28,
              fontFamily: "'Arial Black', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              textAlign: 'center',
              textShadow: shadow,
              margin: 0,
            }}>
              {topic}
            </p>
          )}

          {/* Divider */}
          <div style={{ width: 120, height: 2, background: acc.label, opacity: 0.6, borderRadius: 1 }} />

          {/* Context statement — smaller, italic */}
          {context && (
            <p style={{
              color: acc.context,
              fontSize: 30,
              fontFamily: 'Arial, sans-serif',
              fontStyle: 'italic',
              fontWeight: 400,
              lineHeight: 1.35,
              textAlign: 'center',
              textShadow: shadow,
              margin: 0,
            }}>
              {context}
            </p>
          )}

          {/* THE QUESTION — large, bold, white */}
          <p style={{
            color: acc.question,
            fontSize: qFontSize,
            fontFamily: "'Arial Black', Impact, 'Arial Bold', sans-serif",
            fontWeight: 900,
            lineHeight: 1.18,
            textAlign: 'center',
            textShadow: shadow,
            margin: 0,
          }}>
            {q}
          </p>

          {/* Username — above the template's built-in logo area */}
          {username && (
            <p style={{
              color: acc.label,
              fontSize: 28,
              fontFamily: "'Arial Black', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.1em',
              textAlign: 'center',
              textShadow: shadow,
              margin: 0,
            }}>
              @{username}
            </p>
          )}
        </div>
      </div>
    );
  }
);

IntelShareCard.displayName = 'IntelShareCard';
export default IntelShareCard;
